import React from 'react';
import { Box, Typography, Chip, styled } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { FilterConfiguration, FilterGroup, FilterField } from './types';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

/**
 * Dynamic filter values - supports both string and array values
 * Keys are generated from Sitecore configuration
 * Multi-select dropdowns use string[] while other fields use string
 */
export type FilterValues = Record<string, string | string[]>;

interface ActiveFiltersProps {
  filters: FilterValues;
  onFilterChange: (field: string, value: string) => void;
  totalRecords: number;
  filteredRecords: number;
  filterConfig?: FilterConfiguration;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onClearSort?: () => void;
  columns?: Array<{ id: string; label: string }>;
}

const FiltersContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  display: 'flex',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}));

const FilterLabel = styled(Typography)(({ theme }) => ({
  marginRight: theme.spacing(1),
  alignSelf: 'center',
  fontWeight: 600,
}));

const FilterChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.themeLightColor?.main,
  color: theme.palette.themeColor?.main,
}));

interface FilterConfig {
  key: string; // Now just a string, not constrained to FilterValues keys
  label: string;
  formatValue?: (value: string | string[]) => string;
}

/**
 * Build filter configs dynamically from sitecoreConfig.filterConfig
 * Only processes filters that are defined in the Sitecore configuration
 */
const buildFilterConfigsFromJSON = (
  filterConfig: FilterConfiguration,
  dropdownData?: {
    lmaUsers?: Array<{ userId: string; userName: string }>;
  }
): FilterConfig[] => {
  const configs: FilterConfig[] = [];

  if (!filterConfig?.filterGroups || !filterConfig.enabled) {
    return configs;
  }

  filterConfig.filterGroups.forEach((group: FilterGroup) => {
    if (!group.enabled) return;

    // Handle regular fields
    if (group.fields && group.type !== 'checkbox-card') {
      group.fields.forEach((field: FilterField) => {
        if (!field.enabled) return;

        // Handle slider fields - add both min and max fields to config
        // They will be combined into one chip in the rendering logic
        if (field.type === 'slider' && 'fields' in field) {
          const sliderField = field as any;
          const minField = sliderField.fields?.minField;
          const maxField = sliderField.fields?.maxField;

          // Check if we already added this slider field
          const existingSliderConfig = configs.find(
            (c) => c.key === minField || c.key === maxField
          );

          if (!existingSliderConfig) {
            // Add min field if it exists
            if (minField) {
              configs.push({
                key: minField, // Dynamic key from config
                label: field.label,
                formatValue: (value: string) => value, // Will be handled in rendering
              });
            }
            // Add max field if it exists
            if (maxField) {
              configs.push({
                key: maxField, // Dynamic key from config
                label: field.label,
                formatValue: (value: string) => value, // Will be handled in rendering
              });
            }
          }
        }
        // Handle date range fields — keys are scoped per filter so two dateRange filters stay independent
        else if (field.type === 'dateRange') {
          const fieldId = field.field || field.id;
          const fromKey = `${fieldId}From`;
          const toKey = `${fieldId}To`;
          const alreadyAdded = configs.some((c) => c.key === fromKey || c.key === toKey);
          if (!alreadyAdded) {
            configs.push({
              key: fromKey,
              label: `${field.label} - From`,
              formatValue: (value: string | string[]) => {
                const stringValue = Array.isArray(value) ? value[0] : value;
                try {
                  return new Date(stringValue).toLocaleDateString();
                } catch {
                  return stringValue;
                }
              },
            });
            configs.push({
              key: toKey,
              label: `${field.label} - To`,
              formatValue: (value: string | string[]) => {
                const stringValue = Array.isArray(value) ? value[0] : value;
                try {
                  return new Date(stringValue).toLocaleDateString();
                } catch {
                  return stringValue;
                }
              },
            });
          }
        }
        // Handle date fields
        else if (field.type === 'date') {
          configs.push({
            key: field.field || field.id, // Use field property or fallback to id
            label: field.label,
            formatValue: (value: string | string[]) => {
              const stringValue = Array.isArray(value) ? value[0] : value;
              try {
                return new Date(stringValue).toLocaleDateString();
              } catch {
                return stringValue;
              }
            },
          });
        }
        // Handle regular fields (text, dropdown)
        else {
          configs.push({
            key: field.field || field.id, // Use field property or fallback to id
            label: field.label,
            // Format dropdown values (handle multi-select)
            formatValue:
              field.type === 'dropdown'
                ? (value: string | string[]) => {
                    if (
                      (field.field === 'userIds' || field.id === 'userIds') &&
                      dropdownData?.lmaUsers
                    ) {
                      if (Array.isArray(value)) {
                        return value
                          .map((val) => {
                            const user = dropdownData.lmaUsers?.find(
                              (u) => String(u.userId) === String(val)
                            );
                            return user ? user.userName : val;
                          })
                          .join(', ');
                      }
                      const values = value.split(',').filter((v) => v.trim());
                      if (values.length > 1) {
                        return values
                          .map((val) => {
                            const user = dropdownData.lmaUsers?.find(
                              (u) => String(u.userId) === String(val.trim())
                            );
                            return user ? user.userName : val.trim();
                          })
                          .join(', ');
                      }
                      const user = dropdownData.lmaUsers?.find(
                        (u) => String(u.userId) === String(value)
                      );
                      return user ? user.userName : value;
                    }

                    // Handle array values (multi-select)
                    if (Array.isArray(value)) {
                      const dropdownField = field as any;
                      if (dropdownField.options && Array.isArray(dropdownField.options)) {
                        return value
                          .map((val) => {
                            const option = dropdownField.options.find(
                              (opt: any) => opt.value === val
                            );
                            return option ? option.label : val;
                          })
                          .join(', ');
                      }
                      return value.join(', ');
                    }
                    // Handle string values (single-select or comma-separated legacy format)
                    const values = value.split(',').filter((v) => v.trim());
                    if (values.length > 1) {
                      // For multi-select, map each value to its label if options exist
                      const dropdownField = field as any;
                      if (dropdownField.options && Array.isArray(dropdownField.options)) {
                        return values
                          .map((val) => {
                            const option = dropdownField.options.find(
                              (opt: any) => opt.value === val.trim()
                            );
                            return option ? option.label : val.trim();
                          })
                          .join(', ');
                      }
                      return values.join(', ');
                    }
                    // For single value, find label if options exist
                    const dropdownField = field as any;
                    if (dropdownField.options && Array.isArray(dropdownField.options)) {
                      const option = dropdownField.options.find((opt: any) => opt.value === value);
                      return option ? option.label : value;
                    }
                    return value;
                  }
                : undefined,
          });
        }
      });
    }

    // Handle checkbox groups - ANY checkbox group with a field property
    if (group.checkboxGroups && group.type === 'checkbox-card') {
      group.checkboxGroups.forEach((checkboxGroup) => {
        if (!checkboxGroup.enabled) return;

        // For ALL checkbox fields (dedupe status, lead type, etc.) - combine into one chip
        // This works for any checkbox group that stores values in a single field
        if (checkboxGroup.field) {
          // Check if we already have this field in configs to avoid duplicates
          const existingConfig = configs.find((c) => c.key === checkboxGroup.field);
          if (!existingConfig) {
            configs.push({
              key: checkboxGroup.field, // Dynamic key from config
              label: checkboxGroup.title, // Use the group title as chip label
              formatValue: (value: string | string[]) => {
                const stringValue = Array.isArray(value) ? value.join(',') : value;
                // value is comma-separated (e.g., "new,auto-merge,manual" or "hot,warm")
                const selectedValues = stringValue.split(',').filter((v) => v);
                // Map each value to its display label from the checkbox options
                const labels = selectedValues
                  .map((val) => {
                    const option = checkboxGroup.options.find((opt) => opt.value === val);
                    return option ? option.label : val;
                  })
                  .join(', ');
                return labels;
              },
            });
          }
        }
      });
    }
  });

  return configs;
};

const SortChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.themeLightColor?.main,
  color: theme.palette.themeColor?.main,
}));

export const ActiveSorts: React.FC<{
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onClearSort?: () => void;
  columns?: Array<{ id: string; label: string }>;
}> = ({ sortBy, sortOrder, onClearSort, columns }) => {
  if (!sortBy || !sortOrder) return null;

  const columnLabel = columns?.find((c) => c.id === sortBy)?.label || sortBy;

  return (
    <FiltersContainer>
      <FilterLabel variant="body2">Active Sort:</FilterLabel>
      <SortChip
        icon={
          sortOrder === 'asc' ? (
            <ArrowUpwardIcon style={{ fontSize: 14 }} />
          ) : (
            <ArrowDownwardIcon style={{ fontSize: 14 }} />
          )
        }
        label={`${columnLabel}`}
        size="small"
        onDelete={onClearSort}
        deleteIcon={<CloseIcon />}
      />
    </FiltersContainer>
  );
};

export const ActiveFilters: React.FC<ActiveFiltersProps> = ({
  filters,
  onFilterChange,
  filterConfig,
}) => {
  const lmaDropdownState = useSelector((state: RootState) => state.lmaDropdown.data);

  // Build filter configs dynamically from sitecoreConfig.filterConfig
  // Only show active filters that are defined in Sitecore configuration
  const filterConfigs = filterConfig
    ? buildFilterConfigsFromJSON(filterConfig, lmaDropdownState)
    : [];

  const activeFilterCount = Object.values(filters).filter((v) => v !== '').length;

  if (activeFilterCount === 0) {
    return null;
  }

  // Build all chips dynamically based on active filters from sitecoreConfig
  const allChips: JSX.Element[] = [];
  const processedSliderFields = new Set<string>(); // Track processed slider fields

  filterConfigs.forEach(({ key, label, formatValue }) => {
    const value = filters[key];
    if (!value || value === '' || (Array.isArray(value) && value.length === 0)) return;

    // Special handling for slider fields - combine min/max into one chip
    // Check if this is a slider min/max field by looking at the config
    const isSliderField = filterConfig?.filterGroups.some((group) =>
      group.fields?.some(
        (field) =>
          field.type === 'slider' &&
          'fields' in field &&
          ((field as any).fields?.minField === key || (field as any).fields?.maxField === key)
      )
    );

    if (isSliderField) {
      // Find the slider field config to get the label
      let sliderLabel = label;
      let minField = '';
      let maxField = '';

      filterConfig?.filterGroups.forEach((group) => {
        group.fields?.forEach((field) => {
          if (field.type === 'slider' && 'fields' in field) {
            const sliderField = field as any;
            if (sliderField.fields?.minField === key || sliderField.fields?.maxField === key) {
              sliderLabel = field.label;
              minField = sliderField.fields?.minField || '';
              maxField = sliderField.fields?.maxField || '';
            }
          }
        });
      });

      // Use a unique key for this slider field (based on the field id, not min/max)
      const sliderKey = `${minField}_${maxField}`.replace('Min', '').replace('Max', '');

      // Only process once per slider field
      if (!processedSliderFields.has(sliderKey) && minField && maxField) {
        processedSliderFields.add(sliderKey);

        const minValue = filters[minField];
        const maxValue = filters[maxField];

        // Only show chip if at least one value is set
        if (minValue || maxValue) {
          const cappedMin = minValue ? Math.min(Number(minValue), 100) : null;
          const cappedMax = maxValue ? Math.min(Number(maxValue), 100) : null;

          // Create combined range display
          let rangeText = '';
          if (cappedMin !== null && cappedMax !== null) {
            rangeText = `${cappedMin}% - ${cappedMax}%`;
          } else if (cappedMin !== null) {
            rangeText = `≥${cappedMin}%`;
          } else if (cappedMax !== null) {
            rangeText = `≤${cappedMax}%`;
          }

          allChips.push(
            <FilterChip
              key={sliderKey}
              label={`${sliderLabel}: ${rangeText}`}
              size="small"
              onDelete={() => {
                onFilterChange(minField, '');
                onFilterChange(maxField, '');
              }}
              deleteIcon={<CloseIcon />}
            />
          );
        }
      }
      return; // Skip individual min/max chips
    }

    // For all other filters, show individual chips
    const displayValue = formatValue ? formatValue(value) : value;

    allChips.push(
      <FilterChip
        key={key}
        label={`${label}: ${displayValue}`}
        size="small"
        onDelete={() => onFilterChange(key, '')}
        deleteIcon={<CloseIcon />}
      />
    );
  });

  return (
    <FiltersContainer>
      <FilterLabel variant="body2">Active Filters:</FilterLabel>
      {allChips}
    </FiltersContainer>
  );
};
